import { Button } from '#app/components/ui/button'
import { cn } from '#app/utils/misc'

/**
 * PricingBlock - Pricing table with tiers and feature comparison
 *
 * This component renders a pricing table with multiple tiers.
 * Design reference: TWBlocks Pricing patterns
 *
 * Features:
 * - Multiple pricing tiers
 * - Feature list with checkmarks
 * - Featured/highlighted tier support
 * - CTA buttons per tier
 * - Responsive layout
 */

export interface PricingFeature {
	text?: string
	included?: boolean
}

export interface PricingTier {
	name?: string
	price?: string
	period?: string
	description?: string
	features?: PricingFeature[]
	ctaText?: string
	ctaLink?: string
	featured?: boolean
}

export interface PricingBlockProps {
	title?: string
	subtitle?: string
	tiers?: PricingTier[]
}

export function PricingBlock({
	title = 'Una sola membresía para dominarlo todo',
	subtitle = 'Cancela cuando quieras.',
	tiers = [],
}: PricingBlockProps) {
	// Default tiers if none provided
	const displayTiers: PricingTier[] =
		tiers.length > 0
			? tiers
			: [
					{
						name: 'Membresía Estudio',
						price: '€39',
						period: '/mes',
						description:
							'Ideal para estudios emergentes y profesionales independientes.',
						features: [
							{ text: 'Acceso ilimitado a cursos y roadmaps', included: true },
							{
								text: 'Constructor de Learning Projects con IA',
								included: true,
							},
							{
								text: 'Cohortes en vivo y mentoría comunitaria',
								included: true,
							},
						],
						ctaText: 'Iniciar prueba de 14 días',
						ctaLink: '/signup',
						featured: false,
					},
				]

	return (
		<section className="bg-muted/20 py-16">
			<div className="container mx-auto flex max-w-5xl flex-col gap-8 px-6 text-center">
				{/* Header */}
				<div className="flex flex-col gap-4">
					{title && <h2 className="text-title-2 text-foreground">{title}</h2>}
					{subtitle && (
						<p className="text-muted-foreground mx-auto max-w-2xl text-base md:text-lg">
							{subtitle}
						</p>
					)}
				</div>

				{/* Pricing Tiers */}
				<div className="grid gap-6 md:grid-cols-3">
					{displayTiers.map((tier, index) => (
						<div
							key={index}
							className={cn(
								'border-border bg-background flex h-full flex-col rounded-xl border p-6 text-left shadow-sm',
								tier.featured && 'ring-primary ring-2',
							)}
						>
							{/* Tier Header */}
							<div className="mb-4">
								{tier.name && (
									<h3 className="text-foreground text-xl font-semibold">
										{tier.name}
									</h3>
								)}
								{tier.description && (
									<p className="text-muted-foreground mt-2 text-sm">
										{tier.description}
									</p>
								)}
							</div>

							{/* Price */}
							<div className="mb-4 flex items-baseline gap-1">
								{tier.price && (
									<div className="text-title-1 text-foreground tabular-nums">
										{tier.price}
									</div>
								)}
								{tier.period && (
									<div className="text-muted-foreground text-sm">
										{tier.period}
									</div>
								)}
							</div>

							{/* Features */}
							{tier.features && tier.features.length > 0 && (
								<ul className="text-muted-foreground mb-6 space-y-2 text-sm">
									{tier.features.map((feature, featureIndex) => (
										<li key={featureIndex} className="flex items-start gap-2">
											<span className="text-primary">•</span>
											<span
												className={cn(
													feature.included ? '' : 'line-through opacity-70',
												)}
											>
												{feature.text}
											</span>
										</li>
									))}
								</ul>
							)}

							{/* CTA Button */}
							<div className="mt-auto">
								{tier.ctaText && tier.ctaLink && (
									<Button
										asChild
										variant={tier.featured ? 'default' : 'default'}
										className={cn(
											'bg-primary text-primary-foreground hover:bg-primary/90 w-full rounded-full transition',
										)}
									>
										<a href={tier.ctaLink}>{tier.ctaText}</a>
									</Button>
								)}
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	)
}
